import { forwardRef } from "react"
import { useTranslation } from "react-i18next"
import { ButtonLink } from "@/lib/ButtonLink"
import { PORTAL_EVENTS } from "@/app/router/portalRoutes"
import { getNavItems } from "./navActiveMatchers"
import type { NavbarActiveMap } from "./NavbarDesktopLinks"

export type NavbarMobileMenuProps = {
  id: string
  ariaLabel: string
  isAuthenticated: boolean
  hasAnyAdminAccess: boolean
  activeMap: NavbarActiveMap
  onItemClick: () => void
  onLogout: () => void
}

export const NavbarMobileMenu = forwardRef<HTMLDivElement, NavbarMobileMenuProps>(
  ({ id, ariaLabel, isAuthenticated, hasAnyAdminAccess, activeMap, onItemClick, onLogout }, ref) => {
    const { t } = useTranslation()
    return (
      <div
        ref={ref}
        id={id}
        className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-2"
        role="navigation"
        aria-label={ariaLabel}
      >
        {getNavItems().map((link) => {
          if (isAuthenticated && link.key === "about") return null
          if (!isAuthenticated && link.key === "projects") return null
          if (link.key === "admin" && !(isAuthenticated && hasAnyAdminAccess)) return null
          if (link.key === "events") {
            const eventsPath = isAuthenticated ? PORTAL_EVENTS : "/events"
            return (
              <ButtonLink
                key={link.path}
                to={eventsPath}
                variant="navMobile"
                isActive={activeMap.events}
                onClick={onItemClick}
              >
                {t(`nav.${link.key}`)}
              </ButtonLink>
            )
          }
          if (link.key === "profile") {
            return (
              <ButtonLink
                key={link.path}
                to="/profile"
                variant="navMobile"
                isActive={activeMap.profile}
                onClick={onItemClick}
                aria-label={t("nav.profile")}
              >
                {t("nav.profile")}
              </ButtonLink>
            )
          }
          return (
            <ButtonLink
              key={link.path}
              to={link.path}
              variant="navMobile"
              isActive={activeMap[link.key]}
              onClick={onItemClick}
            >
              {t(`nav.${link.key}`)}
            </ButtonLink>
          )
        })}
        {!isAuthenticated ? (
          <ButtonLink to="/login" variant="navMobileAccent" onClick={onItemClick}>
            {t("nav.loginMobile")}
          </ButtonLink>
        ) : (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <ButtonLink
              variant="navMobile"
              className="!after:hidden text-gray-600 hover:text-brand-500"
              onClick={() => {
                onItemClick()
                onLogout()
              }}
            >
              <span className="inline-flex items-center gap-2">
                <i className="ri-logout-box-r-line text-lg" aria-hidden />
                {t("nav.logout")}
              </span>
            </ButtonLink>
          </div>
        )}
      </div>
    )
  },
)
NavbarMobileMenu.displayName = "NavbarMobileMenu"
